import React, { useContext, useEffect, useState } from 'react';
import { Action, CloseLight, Edit, Input, Modal, NewFolder, Search } from 'esa-ui-library';
import { PropertyType } from 'esa-ui-library/lib/types';

import styles from './ReportTemplates.module.scss';
import filtersStyles from '../../../components/Filters/Filters.module.scss';
import { CREATE_NEW_TEMPLATE, EDIT_TEMPLATE, SAVE } from './ReportTemplates.const';
import { useStore } from '../../../../common';
import { AuthContext } from '../../../../logic';
import { TemplateOption } from './TemplateOption';
import { findIndex, isEmpty } from 'lodash';

const ReportTemplates = (): JSX.Element => {
	const [displayTemplates, setDisplayTemplates] = useState<PropertyType[]>([]);
	const [opened, setOpened] = useState(false);
	const [deleteTemplate, setDeleteTemplate] = useState<PropertyType>();
	const [name, setName] = useState('');
	const [actionType, setActionType] = useState<string>('add');
	const [modalOpened, setModalOpened] = useState<boolean>(false);
	const [isInUse, setIsInUse] = useState<boolean>(false);

	const [state, dispatch] = useStore();
	const { filters, selectedTemplate, templates, savingData } = state;
	const { user, token } = useContext(AuthContext);

	const onChangeName = (value: string) => {
		setName(value);
		if (isInUse) {
			setIsInUse(false);
		}
	};

	const onAddOpenModal = () => {
		setName('');
		setActionType('add');
		setModalOpened(true);
	};

	const onEditOpenModal = () => {
		setName(selectedTemplate.label);
		setActionType('edit');
		setModalOpened(true);
	};

	const onCloseModal = () => {
		setModalOpened(false);
		setIsInUse(false);
	};

	const onCancelDelete = () => {
		setDeleteTemplate(undefined);
	};

	const onAddTemplate = async () => {
		if (findIndex(displayTemplates, (t) => t.label.toLowerCase() === name.toLowerCase()) !== -1) {
			setIsInUse(true);
		} else {
			const payload = { token, name, userId: user?.id, filters };
			dispatch('SAVING', true);
			await dispatch('ADD_TEMPLATE', payload);
			onCloseModal();
		}
	};

	const onEditTemplate = async () => {
		const payload = { token, name, userId: user?.id, filters, id: selectedTemplate.value };
		dispatch('SAVING', true);
		await dispatch('EDIT_TEMPLATE', payload);
		onCloseModal();
	};

	const onSelectTemplate = (t?: PropertyType) => {
		dispatch('SELECT_TEMPLATE', t);
		setOpened(false);
	};

	const onDeleteTemplate = (t: PropertyType) => {
		setDeleteTemplate(t);
		setOpened(false);
	};

	const onConfirmDelete = async () => {
		const payload = { token, id: deleteTemplate?.value, userId: user?.id };
		dispatch('SAVING', true);
		await dispatch('DELETE_TEMPLATE', payload);
		setDeleteTemplate(undefined);
		if (deleteTemplate?.value === selectedTemplate?.value) {
			onSelectTemplate();
		}
	};

	const renderTemplates = (list: PropertyType[]) => {
		if (!list?.length) {
			return (
				<li className={styles.NoData}>
					<div>No Templates</div>
				</li>
			);
		}
		return list?.map((t: PropertyType) => (
			<TemplateOption
				key={t.value}
				template={t}
				onSelectTemplate={onSelectTemplate}
				onDelete={onDeleteTemplate}
				isActive={selectedTemplate?.value === t.value}
			/>
		));
	};

	const getLabelContent = () => (
		<>
			{selectedTemplate?.label}
			<i
				className={filtersStyles.ClearFilter}
				onClick={(e) => {
					e.stopPropagation();
					onSelectTemplate();
				}}
			>
				<CloseLight size={16} />
			</i>
		</>
	);

	useEffect(() => {
		dispatch('TEMPLATES_INIT', { token, userId: user?.id });
	}, []);

	useEffect(() => {
		setDisplayTemplates(templates);
	}, [templates]);

	const modalTitle = actionType === 'edit' ? EDIT_TEMPLATE : CREATE_NEW_TEMPLATE;
	const confirmAction = actionType === 'edit' ? onEditTemplate : onAddTemplate;

	return (
		<div className={styles.Templates}>
			<div className={styles.Buttons}>
				<Action
					controlled
					onDropdownChange={setOpened}
					opened={opened}
					label='Filter Template'
					labelContent={selectedTemplate?.label ? getLabelContent() : ''}
					select
					className={`${styles.Select} ${filtersStyles.Filter} ${selectedTemplate?.label ? filtersStyles.Active : ''}`}
					dataId='action-filter'
				>
					<section data-testid='column-list' onClick={(e) => e.stopPropagation()}>
						<div className={styles.SearchContainer}>
							<Search data={templates} autoFocus searchProperty='label' onSearch={setDisplayTemplates} small />
						</div>
						<ul className={styles.List}>{renderTemplates(displayTemplates)}</ul>
					</section>
				</Action>
				<button
					className={styles.Button}
					data-testid='action-add'
					onClick={onAddOpenModal}
					disabled={filters?.length < 2}
				>
					<span className={styles.ButtonText}>Create New</span>
					<NewFolder />
				</button>
				<button
					className={styles.Button}
					data-testid='action-edit'
					onClick={onEditOpenModal}
					disabled={!selectedTemplate || filters?.length < 2}
				>
					<span className={styles.ButtonText}>Edit Current</span>
					<Edit />
				</button>
			</div>
			{modalOpened && (
				<Modal
					loading={savingData || !name}
					title={modalTitle}
					submitLabel={SAVE}
					primary
					onCancel={onCloseModal}
					onSubmit={confirmAction}
				>
					<Input
						label='Template Name'
						className={styles.Input}
						small
						placeholder='Enter Name'
						value={name}
						onChange={onChangeName}
						error={isInUse}
						errorMessage='The name has already been taken'
					/>
				</Modal>
			)}
			{!isEmpty(deleteTemplate) && (
				<Modal
					loading={savingData}
					title='Delete template'
					submitLabel='Delete'
					danger
					onCancel={onCancelDelete}
					onSubmit={onConfirmDelete}
				>
					{`Are you sure you want to delete ${deleteTemplate.label} Template?`}
				</Modal>
			)}
		</div>
	);
};

export { ReportTemplates };
